import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Modal, Button, Form, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('Administrator');
  const [menus, setMenus] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [activeMenu, setActiveMenu] = useState('');
  const [time, setTime] = useState({ date: '', clock: '' });

  // ==========================================
  // STATE USER DATA & GROUP DATA
  // ==========================================
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // ==========================================
  // STATE MODAL TAMBAH USER
  // ==========================================
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [formErrors, setFormErrors] = useState({});

  const defaultForm = {
    username: '',
    nama: '',
    email: '',
    password: '',
    confirmPassword: '',
    level: '2',
    group: '',
    kd_lokasi: '',
    nm_lokasi: '',
    aktif: '1',
  };
  const [formData, setFormData] = useState(defaultForm);
  const [showPassword, setShowPassword] = useState(false);

  // ==========================================
  // STATE PENCARIAN LOKASI & GROUP USER
  // ==========================================
  const [lokasiQuery, setLokasiQuery]       = useState('');
  const [lokasiResults, setLokasiResults]   = useState([]);
  const [isLoadingLokasi, setIsLoadingLokasi] = useState(false);
  const [showLokasiDrop, setShowLokasiDrop] = useState(false);
  const lokasiRef = useRef(null);
  const debounceRef = useRef(null);

  const [groupQuery, setGroupQuery]         = useState('');
  const [showGroupDrop, setShowGroupDrop]   = useState(false);
  const groupRef = useRef(null);

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);

    const savedMenu = localStorage.getItem('mainmenu');
    if (savedMenu) {
      const parsedMenu = JSON.parse(savedMenu);
      setMenus(parsedMenu);
      if (parsedMenu.length > 0) setActiveMenu(parsedMenu[0].id_mn);
    }

    const updateTime = () => {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: now.toLocaleDateString('id-ID', options),
        clock: now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      });
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================
  // FETCH DATA USERS
  // ==========================================
  const fetchUsers = async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/show_users?token=${encodeURIComponent(token)}`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const json = await response.json();
      setUsers(json.data || []);
    } catch (err) {
      setFetchError('Gagal mengambil data user: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // FETCH DATA GROUPS
  // ==========================================
  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/show_group?token=${encodeURIComponent(token)}`);
      const json = await response.json();
      setGroups(json.list_group || json.data || []);
    } catch (err) {
      console.error('Gagal mengambil data group:', err);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // ==========================================
  // FETCH DATA GUDANG / LOKASI
  // ==========================================
  const fetchGudang = useCallback(async (q) => {
    const apiUrl = getApiUrl();
    const token  = localStorage.getItem('token');
    setIsLoadingLokasi(true);
    try {
      const res  = await fetch(`${apiUrl}/show_gudang?q=${encodeURIComponent(q)}&token=${encodeURIComponent(token)}`);
      const json = await res.json();
      setLokasiResults(json.data || []);
      setShowLokasiDrop(true);
    } catch {
      setLokasiResults([]);
    } finally {
      setIsLoadingLokasi(false);
    }
  }, []);

  // Tutup dropdown saat klik di luar
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (lokasiRef.current && !lokasiRef.current.contains(e.target)) {
        setShowLokasiDrop(false);
      }
      if (groupRef.current && !groupRef.current.contains(e.target)) {
        setShowGroupDrop(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // ==========================================
  // HANDLERS FORM
  // ==========================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handler khusus field lokasi dengan debounce
  const handleLokasiInput = (e) => {
    const val = e.target.value;
    setLokasiQuery(val);
    setFormData(prev => ({ ...prev, nm_lokasi: val, kd_lokasi: '' }));
    if (formErrors.nm_lokasi) setFormErrors(prev => ({ ...prev, nm_lokasi: '' }));
    clearTimeout(debounceRef.current);
    if (val.trim().length >= 1) {
      debounceRef.current = setTimeout(() => fetchGudang(val.trim()), 350);
    } else {
      setLokasiResults([]);
      setShowLokasiDrop(false);
    }
  };

  const handleSelectLokasi = (item) => {
    setFormData(prev => ({ ...prev, kd_lokasi: item.kode, nm_lokasi: item.nama }));
    setLokasiQuery(item.nama);
    setLokasiResults([]);
    setShowLokasiDrop(false);
    if (formErrors.nm_lokasi) setFormErrors(prev => ({ ...prev, nm_lokasi: '' }));
  };

  // Handler pencarian Group User
  const handleGroupInput = (e) => {
    const val = e.target.value;
    setGroupQuery(val);
    setFormData(prev => ({ ...prev, group: '' }));
    setShowGroupDrop(true);
  };

  const handleSelectGroup = (item) => {
    setFormData(prev => ({ ...prev, group: item.kode }));
    setGroupQuery(`${item.kode} - ${item.nama}`);
    setShowGroupDrop(false);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) errors.username = 'Username wajib diisi.';
    if (!formData.nama.trim()) errors.nama = 'Nama lengkap wajib diisi.';
    if (!formData.email.trim()) errors.email = 'Email wajib diisi.';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Format email tidak valid.';
    if (!formData.password) errors.password = 'Password wajib diisi.';
    else if (formData.password.length < 6) errors.password = 'Password minimal 6 karakter.';
    if (!formData.confirmPassword) errors.confirmPassword = 'Konfirmasi password wajib diisi.';
    else if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Password tidak cocok.';
    if (!formData.nm_lokasi.trim()) errors.nm_lokasi = 'Lokasi/Apotek wajib diisi.';
    return errors;
  };

  const handleOpenModal = () => {
    setFormData(defaultForm);
    setFormErrors({});
    setSaveMessage({ type: '', text: '' });
    setShowPassword(false);
    setLokasiQuery('');
    setLokasiResults([]);
    setShowLokasiDrop(false);
    setGroupQuery('');
    setShowGroupDrop(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormErrors({});
    setSaveMessage({ type: '', text: '' });
    setLokasiQuery('');
    setLokasiResults([]);
    setShowLokasiDrop(false);
    setGroupQuery('');
    setShowGroupDrop(false);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    const errors = validateForm();

    // Validasi tambahan: kd_lokasi wajib dipilih dari dropdown
    if (!formData.kd_lokasi) {
      errors.nm_lokasi = 'Pilih lokasi dari daftar pencarian.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: '', text: '' });

    try {
      const apiUrl = getApiUrl();
      const token  = localStorage.getItem('token');

      // Backend menggunakan request.form → kirim sebagai FormData
      const formPayload = new FormData();
      formPayload.append('usrname',   formData.username);
      formPayload.append('passwd',    formData.password);
      formPayload.append('nama',      formData.nama);
      formPayload.append('email',     formData.email);
      formPayload.append('level',     formData.level);
      formPayload.append('group',     formData.group);
      formPayload.append('groupid',   formData.group);
      formPayload.append('group_id',  formData.group);
      formPayload.append('kd_lokasi', formData.kd_lokasi);
      formPayload.append('token',     token);

      const response = await fetch(`${apiUrl}/simpan_user?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        body: formPayload,   // FormData — JANGAN set Content-Type manual
      });

      const json = await response.json();

      if (json.status === 1) {
        setSaveMessage({ type: 'success', text: json.msg || 'User berhasil ditambahkan!' });
        fetchUsers();
        setTimeout(() => handleCloseModal(), 1800);
      } else {
        // status === 0: username sudah ada atau gagal
        setSaveMessage({ type: 'danger', text: json.msg || 'Gagal menyimpan user.' });
      }
    } catch (err) {
      setSaveMessage({ type: 'danger', text: 'Gagal terhubung ke server: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const getMenuIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('master')) return 'bi-folder2-open';
    if (n.includes('gudang')) return 'bi-box-seam';
    if (n.includes('keuangan')) return 'bi-wallet2';
    if (n.includes('admin')) return 'bi-shield-lock';
    if (n.includes('laporan')) return 'bi-file-earmark-bar-graph';
    return 'bi-grid-1x2';
  };

  // Filter user berdasarkan search
  const filteredUsers = users.filter(u => {
    const q = searchQuery.toLowerCase();
    return (
      u.username?.toLowerCase().includes(q) ||
      u.nama?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.nm_lokasi?.toLowerCase().includes(q)
    );
  });

  const getLevelBadge = (level) => {
    if (level === 1) return <Badge bg="danger" style={{ fontSize: '0.65rem' }}>Super Admin</Badge>;
    if (level === 2) return <Badge bg="primary" style={{ fontSize: '0.65rem' }}>Admin</Badge>;
    if (level === 3) return <Badge bg="success" style={{ fontSize: '0.65rem' }}>Operator</Badge>;
    return <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Level {level}</Badge>;
  };

  const getStatusBadge = (aktif) => {
    return aktif === '1'
      ? <Badge style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.65rem', border: '1px solid #86efac' }}>Aktif</Badge>
      : <Badge style={{ background: '#fee2e2', color: '#b91c1c', fontSize: '0.65rem', border: '1px solid #fca5a5' }}>Non-Aktif</Badge>;
  };

  // Inline error helper
  const FieldError = ({ msg }) => msg ? (
    <div className="d-flex align-items-center gap-1 mt-1" style={{ color: '#ef4444', fontSize: '0.7rem' }}>
      <i className="bi bi-exclamation-circle-fill"></i>
      <span>{msg}</span>
    </div>
  ) : null;

  return (
    <div style={styles.appContainer}>
      {/* Navbar */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home">
            <i className="bi bi-layers-half me-2 text-warning"></i>
            <span className="fw-bold text-white">Solusi Integrasi Layanan Kesehatan ( SILK )</span>
          </a>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 flex-row gap-2">
              {menus.map((item) => {
                const isActive = location.pathname === item.lnk;
                return (
                  <li className="nav-item" key={item.id_mn}>
                    <a
                      className={`nav-link-custom ${isActive ? 'active' : ''}`}
                      href={item.lnk}
                      style={{ ...styles.navLinkCustom, ...(isActive ? styles.navLinkActive : {}) }}
                    >
                      <i className={`bi ${getMenuIcon(item.des_mn)}`}></i>
                      {item.des_mn?.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center text-white text-end gap-3 ms-auto">
              <div className="small border-end pe-3 border-light border-opacity-25 d-none d-sm-block">
                <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>{time.date}</div>
                <div className="text-warning" style={{ fontSize: '0.7rem' }}>{time.clock}</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 30, height: 30, background: 'rgba(13,148,136,0.3)', border: '1px solid rgba(20,184,166,0.5)' }}>
                  <i className="bi bi-person-fill" style={{ fontSize: '0.85rem' }}></i>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{username}</div>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Online</div>
                </div>
              </div>
              <button onClick={() => navigate('/home')} className="btn btn-sm text-white d-flex align-items-center gap-1"
                style={{ background: 'rgba(13,148,136,0.2)', border: '1px solid rgba(20,184,166,0.4)', fontSize: '0.72rem', borderRadius: 6 }}>
                <i className="bi bi-house-fill"></i> Beranda
              </button>
            </div>
          </div>
        </Container>
      </nav>

      {/* Main Content */}
      <div style={styles.mainContent}>
        <Row className="g-3">
          <Col lg={12}>
            <Card style={styles.mainCard}>
              {/* Card Header */}
              <Card.Header style={{ ...styles.cardHeader, ...styles.bgMenuHeader }} className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-people-fill"></i>
                  <span>MANAJEMEN USER</span>
                  {location.state?.info && (
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.75)',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      padding: '1px 10px',
                    }}>
                      <i className="bi bi-info-circle me-1"></i>{location.state.info}
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge rounded-pill fw-normal px-2" style={{ background: 'rgba(255,255,255,0.15)', fontSize: '0.7rem' }}>
                    {filteredUsers.length} User
                  </span>
                </div>
              </Card.Header>

              <Card.Body style={{ padding: '1rem' }}>
                {/* Toolbar */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                    <InputGroup.Text style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b' }}>
                      <i className="bi bi-search" style={{ fontSize: '0.75rem' }}></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Cari username, nama, email, lokasi..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ fontSize: '0.8rem', border: '1px solid #e2e8f0', borderLeft: 'none' }}
                    />
                    {searchQuery && (
                      <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery('')} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>

                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={fetchUsers} disabled={isLoading}
                      style={{ fontSize: '0.75rem' }}>
                      <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                      Refresh
                    </Button>
                    <Button size="sm" onClick={handleOpenModal} className="text-white fw-semibold"
                      style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', border: 'none', fontSize: '0.75rem', boxShadow: '0 2px 8px rgba(13,148,136,0.3)' }}>
                      <i className="bi bi-person-plus-fill me-1"></i> Tambah User Baru
                    </Button>
                  </div>
                </div>

                {/* Error Banner */}
                {fetchError && (
                  <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>{fetchError}</span>
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={fetchUsers} style={{ fontSize: '0.7rem' }}>
                      Coba Lagi
                    </button>
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive rounded-2 border" style={{ maxHeight: '58vh' }}>
                  <Table hover size="sm" className="align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                    <thead className="sticky-top" style={{ zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(90deg, #0f172a, #1e293b)', color: '#ffffff', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        <th className="px-3 py-2" style={{ color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>No</th>
                        <th className="px-3 py-2" style={{ color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>ID User</th>
                        <th className="px-3 py-2" style={{ color: '#cbd5e1', fontWeight: 700, whiteSpace: 'nowrap' }}>Username</th>
                        <th className="px-3 py-2" style={{ color: '#cbd5e1', fontWeight: 700, whiteSpace: 'nowrap' }}>Nama Lengkap</th>
                        <th className="px-3 py-2" style={{ color: '#cbd5e1', fontWeight: 700, whiteSpace: 'nowrap' }}>Email</th>
                        <th className="px-3 py-2" style={{ color: '#cbd5e1', fontWeight: 700, whiteSpace: 'nowrap' }}>Lokasi / Apotek</th>
                        <th className="px-3 py-2 text-center" style={{ color: '#cbd5e1', fontWeight: 700, whiteSpace: 'nowrap' }}>Level</th>
                        <th className="px-3 py-2 text-center" style={{ color: '#34d399', fontWeight: 800, whiteSpace: 'nowrap' }}>Status</th>
                        <th className="px-3 py-2 text-center" style={{ color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>Tgl Registrasi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={9} className="text-center py-5">
                            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                            <span className="text-muted" style={{ fontSize: '0.82rem' }}>Memuat data user...</span>
                          </td>
                        </tr>
                      ) : filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-5 text-muted">
                            <i className="bi bi-people fs-3 d-block mb-2 text-secondary opacity-50"></i>
                            {searchQuery ? `Tidak ada user yang cocok dengan "${searchQuery}"` : 'Belum ada data user.'}
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user, idx) => (
                          <tr key={user.id_user}
                            style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                            <td className="px-3 text-muted" style={{ width: 40 }}>{idx + 1}</td>
                            <td className="px-3 font-monospace text-muted" style={{ fontSize: '0.72rem' }}>{user.id_user}</td>
                            <td className="px-3">
                              <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{ width: 28, height: 28, background: 'linear-gradient(135deg, #0d9488, #0f766e)', color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                                  {(user.nama || user.username || '?').charAt(0).toUpperCase()}
                                </div>
                                <span className="fw-semibold text-dark">{user.username}</span>
                              </div>
                            </td>
                            <td className="px-3 fw-medium">{user.nama}</td>
                            <td className="px-3 text-muted">{user.email}</td>
                            <td className="px-3">
                              <span className="badge fw-normal" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '0.65rem' }}>
                                <i className="bi bi-geo-alt-fill me-1"></i>{user.nm_lokasi}
                              </span>
                            </td>
                            <td className="px-3 text-center">{getLevelBadge(user.level)}</td>
                            <td className="px-3 text-center">{getStatusBadge(user.aktif)}</td>
                            <td className="px-3 text-center text-muted" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                              {user.tglreg ? new Date(user.tglreg).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: '2-digit' }) : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Footer Info */}
                <div className="d-flex align-items-center justify-content-between mt-2" style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  <span>Menampilkan <strong>{filteredUsers.length}</strong> dari <strong>{users.length}</strong> user</span>
                  <span>Terakhir diperbarui: {time.clock}</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ============================================
          MODAL TAMBAH USER BARU
      ============================================ */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="py-2 px-3 text-white"
          style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' }}>
          <Modal.Title style={{ fontSize: '0.9rem' }} className="fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-person-plus-fill"></i> Tambah User Baru
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSaveUser}>
          <Modal.Body className="p-0" style={{ fontSize: '0.8rem' }}>

            {saveMessage.text && (
              <div className={`alert alert-${saveMessage.type} d-flex align-items-center gap-2 mx-3 mt-3 mb-0 py-2`} style={{ fontSize: '0.8rem' }}>
                <i className={`bi bi-${saveMessage.type === 'success' ? 'check-circle-fill' : 'exclamation-triangle-fill'}`}></i>
                <span>{saveMessage.text}</span>
              </div>
            )}

            {/* SEKSI 1: IDENTITAS */}
            <div className="px-3 pt-3 pb-2">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="badge text-white" style={{ background: '#0d9488', fontSize: '0.65rem' }}>1</span>
                <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem', color: '#475569', letterSpacing: '0.8px' }}>Identitas User</span>
                <hr className="flex-grow-1 my-0 border-secondary opacity-25" />
              </div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Username <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="text" name="username"
                      value={formData.username} onChange={handleInputChange}
                      placeholder="Contoh: johndoe"
                      isInvalid={!!formErrors.username}
                      style={formStyles.input} />
                    <FieldError msg={formErrors.username} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Nama Lengkap <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="text" name="nama"
                      value={formData.nama} onChange={handleInputChange}
                      placeholder="Nama lengkap user"
                      isInvalid={!!formErrors.nama}
                      style={formStyles.input} />
                    <FieldError msg={formErrors.nama} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="email" name="email"
                      value={formData.email} onChange={handleInputChange}
                      placeholder="email@domain.com"
                      isInvalid={!!formErrors.email}
                      style={formStyles.input} />
                    <FieldError msg={formErrors.email} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Group User</Form.Label>
                    <div className="position-relative" ref={groupRef}>
                      <div className="input-group input-group-sm">
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ketik kode / nama group..."
                          value={groupQuery}
                          onChange={handleGroupInput}
                          onFocus={() => setShowGroupDrop(true)}
                          autoComplete="off"
                          style={{
                            ...formStyles.input,
                            borderColor: formData.group ? '#0d9488' : undefined,
                            paddingRight: '2rem'
                          }}
                        />
                        <span className="input-group-text" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: 'none', padding: '0 0.5rem' }}>
                          {isLoadingGroups
                            ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem', color: '#0d9488' }} />
                            : <i className="bi bi-people-fill" style={{ fontSize: '0.75rem', color: formData.group ? '#0d9488' : '#94a3b8' }}></i>
                          }
                        </span>
                      </div>

                      {/* Kode group terpilih */}
                      {formData.group && (
                        <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '0.68rem', color: '#0d9488' }}>
                          <i className="bi bi-check-circle-fill"></i>
                          <span>Kode Group: <strong>{formData.group}</strong></span>
                        </div>
                      )}

                      {/* Dropdown hasil pencarian group */}
                      {showGroupDrop && (
                        <div
                          className="position-absolute w-100 rounded-2 shadow-lg"
                          style={{
                            top: '100%', left: 0, zIndex: 9999,
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            maxHeight: '200px', overflowY: 'auto',
                            marginTop: '2px'
                          }}
                        >
                          {groups
                            .filter(g =>
                              !groupQuery ||
                              g.kode.toLowerCase().includes(groupQuery.toLowerCase()) ||
                              g.nama.toLowerCase().includes(groupQuery.toLowerCase())
                            )
                            .map((item) => (
                              <div
                                key={item.kode}
                                onClick={() => handleSelectGroup(item)}
                                className="d-flex align-items-center gap-2 px-3 py-2"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '0.78rem',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.1s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <span className="badge fw-normal flex-shrink-0" style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.6rem', minWidth: '3.5rem' }}>
                                  {item.kode}
                                </span>
                                <span className="text-dark fw-medium">{item.nama}</span>
                              </div>
                            ))}

                          {groups.filter(g =>
                            !groupQuery ||
                            g.kode.toLowerCase().includes(groupQuery.toLowerCase()) ||
                            g.nama.toLowerCase().includes(groupQuery.toLowerCase())
                          ).length === 0 && !isLoadingGroups && (
                            <div className="text-center py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                              <i className="bi bi-search me-1"></i>Tidak ada group ditemukan
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Lokasi / Apotek <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative" ref={lokasiRef}>
                      <div className="input-group input-group-sm">
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ketik nama lokasi / apotek..."
                          value={lokasiQuery}
                          onChange={handleLokasiInput}
                          onFocus={() => { if (lokasiResults.length > 0) setShowLokasiDrop(true); }}
                          isInvalid={!!formErrors.nm_lokasi}
                          autoComplete="off"
                          style={{
                            ...formStyles.input,
                            borderColor: formErrors.nm_lokasi ? '#ef4444' : (formData.kd_lokasi ? '#0d9488' : undefined),
                            paddingRight: '2rem'
                          }}
                        />
                        <span className="input-group-text" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: 'none', padding: '0 0.5rem' }}>
                          {isLoadingLokasi
                            ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem', color: '#0d9488' }} />
                            : <i className="bi bi-geo-alt-fill" style={{ fontSize: '0.75rem', color: formData.kd_lokasi ? '#0d9488' : '#94a3b8' }}></i>
                          }
                        </span>
                      </div>

                      {/* Kode lokasi terpilih */}
                      {formData.kd_lokasi && (
                        <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '0.68rem', color: '#0d9488' }}>
                          <i className="bi bi-check-circle-fill"></i>
                          <span>Kode: <strong>{formData.kd_lokasi}</strong></span>
                        </div>
                      )}

                      {/* Dropdown hasil pencarian */}
                      {showLokasiDrop && lokasiResults.length > 0 && (
                        <div
                          className="position-absolute w-100 rounded-2 shadow-lg"
                          style={{
                            top: '100%', left: 0, zIndex: 9999,
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            maxHeight: '200px', overflowY: 'auto',
                            marginTop: '2px'
                          }}
                        >
                          {lokasiResults.map((item) => (
                            <div
                              key={item.kode}
                              onClick={() => handleSelectLokasi(item)}
                              className="d-flex align-items-center gap-2 px-3 py-2"
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.78rem',
                                borderBottom: '1px solid #f1f5f9',
                                transition: 'background 0.1s'
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            >
                              <span className="badge fw-normal flex-shrink-0" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.6rem', minWidth: '3.5rem' }}>
                                {item.kode}
                              </span>
                              <span className="text-dark">{item.nama}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tidak ada hasil */}
                      {showLokasiDrop && !isLoadingLokasi && lokasiResults.length === 0 && lokasiQuery.trim().length >= 1 && (
                        <div className="position-absolute w-100 rounded-2 shadow-sm text-center py-2"
                          style={{ top: '100%', left: 0, zIndex: 9999, background: '#fff', border: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                          <i className="bi bi-search me-1"></i>Tidak ada lokasi ditemukan
                        </div>
                      )}
                    </div>
                    <FieldError msg={formErrors.nm_lokasi} />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <hr className="my-0 border-secondary opacity-15" />

            {/* SEKSI 2: KEAMANAN */}
            <div className="px-3 py-2">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="badge text-white" style={{ background: '#7c3aed', fontSize: '0.65rem' }}>2</span>
                <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem', color: '#475569', letterSpacing: '0.8px' }}>Keamanan & Akses</span>
                <hr className="flex-grow-1 my-0 border-secondary opacity-25" />
              </div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Password <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <Form.Control size="sm" type={showPassword ? 'text' : 'password'} name="password"
                        value={formData.password} onChange={handleInputChange}
                        placeholder="Min. 6 karakter"
                        isInvalid={!!formErrors.password}
                        style={{ ...formStyles.input, paddingRight: '2rem' }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px', fontSize: '0.85rem' }}>
                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                      </button>
                    </div>
                    <FieldError msg={formErrors.password} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Konfirmasi Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type={showPassword ? 'text' : 'password'} name="confirmPassword"
                      value={formData.confirmPassword} onChange={handleInputChange}
                      placeholder="Ulangi password"
                      isInvalid={!!formErrors.confirmPassword}
                      style={formStyles.input} />
                    <FieldError msg={formErrors.confirmPassword} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Level Akses</Form.Label>
                    <Form.Select size="sm" name="level"
                      value={formData.level} onChange={handleInputChange}
                      style={formStyles.input}>
                      <option value="1">1 – Super Admin</option>
                      <option value="2">2 – Admin</option>
                      <option value="3">3 – Operator</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={formStyles.label}>Status Akun</Form.Label>
                    <Form.Select size="sm" name="aktif"
                      value={formData.aktif} onChange={handleInputChange}
                      style={formStyles.input}>
                      <option value="1">Aktif</option>
                      <option value="0">Non-Aktif</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

          </Modal.Body>

          <Modal.Footer className="py-2 px-3" style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button variant="outline-secondary" size="sm" onClick={handleCloseModal} className="px-3" disabled={isSaving}>
              <i className="bi bi-x-lg me-1"></i>Batal
            </Button>
            <Button size="sm" type="submit" disabled={isSaving} className="px-3 text-white fw-semibold"
              style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', border: 'none', boxShadow: '0 2px 8px rgba(13,148,136,0.3)' }}>
              {isSaving ? (
                <><Spinner animation="border" size="sm" className="me-1" />Menyimpan...</>
              ) : (
                <><i className="bi bi-person-check-fill me-1"></i>Simpan User</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

// ============================================
// STYLES
// ============================================
const styles = {
  appContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    overflow: 'hidden',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.45rem 1.2rem',
    flexShrink: 0,
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.15)',
  },
  navLinkCustom: {
    color: 'rgba(226, 232, 240, 0.75)',
    fontSize: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.4rem 0.75rem',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
  navLinkActive: {
    background: 'rgba(13, 148, 136, 0.3)',
    border: '1px solid rgba(20, 184, 166, 0.4)',
    fontWeight: '600',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(13, 148, 136, 0.25)',
  },
  mainContent: {
    flex: 1,
    padding: '1rem 1.5rem',
    overflowY: 'auto',
  },
  mainCard: {
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08)',
    background: '#ffffff',
  },
  cardHeader: {
    padding: '0.65rem 1.2rem',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.85rem',
    letterSpacing: '0.3px',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  },
};

const formStyles = {
  label: {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '0.15rem',
    display: 'block',
    letterSpacing: '0.2px',
  },
  input: {
    fontSize: '0.8rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    color: '#1e293b',
  },
};

export default UserManagement;
